/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo } from "react";
import { UseYetiStats } from "../../hooks/company";
import { UseIsAdmin, UseUserDetails } from "../../hooks/auth";
import { UseTopPlayers } from "../../hooks/kpi";
import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent } from "../../helpers/scoreAggregate";
import "./yetiStyles.css";

// Components
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import MiniChart from "./MiniChart";
import MetricInfoTooltip from "./MetricInfoTooltip";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import useMediaQuery from "@mui/material/useMediaQuery";

// periodIdx → the yeti-score bucket, mirroring the dashboard filter order.
const IDX_TO_BUCKET = { 0: "yesterday", 1: "today", 2: "week", 4: "lastWeek", 3: "month", 5: "lastMonth" };

const ItemBox = React.forwardRef(
  ({ kpi, amount: playerAmt, points, isTimeline = false, isSelected = null, darkMode = false, periodIdx = 3, yetiStats = null, ...rest }, ref) => {
    const bucket = IDX_TO_BUCKET[periodIdx] || "month";
    const yetiBucketKey = bucket === "lastMonth" ? "month" : bucket;
    // These tiles show whole numbers only, e.g. $14,858.44 → $14,858 and
    // 4,571.48 → 4,571. Percentage/rate metrics (kpi.kind === "%") are stored
    // as fractions (0.4314 = 43.14%), so rounding the raw fraction would
    // collapse a real value to 0% or 100% — round to the nearest WHOLE
    // PERCENT instead (0.4314 → 0.43 → "43%"). Quality Bid Accuracy is the one
    // exception — it keeps one decimal (0.9733 → "97.3%") everywhere it shows.
    const fmt = (val) => {
      if (kpi.kind === "%") {
        return formatKpiPercent(kpi, Number(val) || 0);
      }
      return formatNumber(Math.round(Number(val) || 0), kpi.kind);
    };
    const [isLeading, setIsLeading] = React.useState(true);
    const [amount, setAmount] = React.useState(0);
    const [plus, setPlus] = React.useState("");
    const [yetiDiff, setYetiDiff] = React.useState(0);
    // Prefer the yeti pace the card already combined across its scoped
    // workspaces (avg/sum, alias-keyed for Custom metric-plan columns) —
    // calling the global single-workspace hook here instead is what left the
    // goal reading 0 whenever this tile was scoped to something other than
    // the currently-selected nav workspace, or combined 2+ workspaces.
    // Surfaces that render ItemBox outside a scoped card (Timeline, Scorecard)
    // pass no yetiStats, so they keep falling back to the live hook.
    const globalYeti = UseYetiStats();
    const yeti = yetiStats || globalYeti;
    const isSmallScreen = useMediaQuery((themes) => themes.breakpoints.down("md"));

    useEffect(() => {
      if (yeti) {
        // Compare against the yeti's pace for the SAME window the tile shows —
        // the yeti stats are already prorated per bucket (today = daily target,
        // week / month = business-days elapsed).
        const yetiBucket = yeti[yetiBucketKey] || yeti.month;
        const versusYeti = points - yetiBucket[kpi._id]?.pts;

        // Original value
        let yetiDifference = playerAmt - yetiBucket[kpi._id]?.amt;

        // Since we append the "plus" sign if the "versusYeti" (points) is greater than 0,
        // let this sign take precedence over the negative "yetiDifference" (amount) value
        // Note: This behaviour only happens in Negative direction anchor metrics.
        // The direction check is load-bearing, not decorative — without it, a
        // points/amount divergence on a HIGHER-is-better anchor metric (e.g.
        // Closed Rate averaged across workspaces where one workspace's
        // negative avgPts got excluded from the cross-workspace points mean
        // while its rate stayed in the amount mean) could flip a genuinely
        // below-benchmark amount to a green "+", praising underperformance.
        if (
          kpi?.metricType === "anchor" &&
          (kpi?.direction ?? 1) < 0 &&
          versusYeti > 0 &&
          yetiDifference < 0
        ) {
          // Make the yetiDifference positive to remove its negative (-) sign
          yetiDifference *= -1;
        }

        // Green/red must follow the NUMBER the tile displays (the amount
        // delta), not the points delta. Points and amounts can disagree
        // (different scales vs the yeti benchmark), which colored a negative
        // delta like "-$6,812" green. Below goal → red, at/above → green.
        setPlus(yetiDifference > 0 ? "+" : "");
        setIsLeading(yetiDifference >= 0);

        setYetiDiff(yetiDifference);
        setAmount(playerAmt);
      }
    }, [kpi, yeti, playerAmt, points, bucket]);

    // Yellow outline only when genuinely above the yeti. `yetiDiff > 0`
    // reflects the amount delta after the anchor-metric sign flip, so it
    // matches the green/red number the user sees in the card.
    const isAboveYeti = yetiDiff > 0;

    // Crown = KPI KING, not "hitting goal". It marks the player with the
    // highest amount for this metric in the workspace this month, and only
    // on that player's own dashboard. Admins see team averages on these
    // tiles, so the crown never shows for them. Negative-direction metrics
    // (lower is better) don't crown a "most of" leader.
    const isAdmin = UseIsAdmin();
    const currentUser = UseUserDetails();
    const topPlayers = UseTopPlayers(bucket);
    const isKpiKing = useMemo(() => {
      if (isAdmin || isTimeline) return false;
      if ((kpi.direction ?? 1) < 0) return false;
      const scorers = (topPlayers || []).filter(
        (p) =>
          p.isActive !== false &&
          p.lastName !== "Yeti" &&
          (p.scores?.[kpi._id]?.amt || 0) > 0,
      );
      if (!scorers.length) return false;
      const king = scorers.reduce((best, p) =>
        (p.scores?.[kpi._id]?.amt || 0) > (best.scores?.[kpi._id]?.amt || 0)
          ? p
          : best,
      );
      return String(king.userId) === String(currentUser?._id);
    }, [isAdmin, isTimeline, kpi._id, kpi.direction, topPlayers, currentUser?._id]);

    // useEffect(() => {
    //   if (kpi.gap) {
    //     // Change display value if KPI has gap calculation
    //     setAmount((playerAmt - kpi.target) * kpi.direction);
    //   }
    // }, [kpi, playerAmt]);

    return (
      <SoftBox
        ref={ref}
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          borderRadius: "12px",
          // Clip the absolutely-positioned sparkline canvas to the tile's
          // rounded corners — at narrow widths it could bleed past the edge.
          overflow: "hidden",
          // Thin hairline + a very light lift shadow (Victor asked the shadow
          // back, softer). Kept subtle and identical per tile so it doesn't
          // read inconsistently like the old heavy one.
          border: "1px solid #EEF1F5",
          boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
          ...(isTimeline && isSmallScreen ? { minHeight: "104px" } : {}),
          ...(isAboveYeti && points && !isTimeline ? { border: '1px solid #FFDD00' } : {}),
          ...(isTimeline ? { border: '1px solid #D0D5DD' } : {}),
          ...(isSelected && isSelected === kpi._id ? { 
            border: "2px solid transparent",
            borderRadius: "12px",
            background: "linear-gradient(#fff, #fff) padding-box, linear-gradient(to right, #7928CA 0%, #FF0080 100%) border-box"
          } : { background: darkMode ? "#21262D" : "#fff" })
        }}
        {...rest}
      >
        <SoftBox
          lineHeight={1}
          className="flex flex-col justify-between h-full"
          sx={{
            position: "relative",
            px: isTimeline && isSmallScreen ? "12px" : "8%",
            py: isTimeline && isSmallScreen ? "10px" : "5%",
            gap: isTimeline && isSmallScreen ? "6px" : 0,
          }}
        >
          <SoftBox
            className="flex items-center h-[15%]"
            sx={isTimeline && isSmallScreen ? { height: "auto", minHeight: "32px", alignItems: "flex-start" } : undefined}
          >
            <SoftTypography
              sx={{
                // Tile label — unified token: 12.5/500 slate.
                fontFamily: "DM Sans",
                color: !darkMode ? "#667085" : "#FFFFFF",
                fontWeight: 500,
                fontSize: { mini: "12.5px", wide: "1.5vh" },
                letterSpacing: "0.005em",
                lineHeight: 1.25,
                ...(isTimeline && isSmallScreen && {
                  width: "100%",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                }),
              }}
            >
              {kpi.title}{" "}
              {kpi.isShared && (
                <Diversity3Icon className="mt-[-5%] h-[1.5vh] w-auto" />
              )}
            </SoftTypography>
            {/* Explains the metric's gamification in the player's own terms —
                how points are earned and the goal. */}
            <MetricInfoTooltip kpi={kpi} darkMode={darkMode} />
            {isKpiKing && (
                <SoftBox sx={{marginLeft: '15px!important'}}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_105_18)">
                    <circle cx="12" cy="12" r="12" fill="#FFF8CB"/>
                    <path d="M6.75001 18.75H17.25M11.6715 5.4495C11.7039 5.3907 11.7514 5.34167 11.8092 5.30752C11.867 5.27338 11.9329 5.25536 12 5.25536C12.0671 5.25536 12.133 5.27338 12.1908 5.30752C12.2486 5.34167 12.2961 5.3907 12.3285 5.4495L14.5425 9.6525C14.5953 9.74982 14.669 9.83424 14.7583 9.89971C14.8476 9.96518 14.9503 10.0101 15.059 10.0311C15.1677 10.0522 15.2797 10.049 15.387 10.0216C15.4943 9.99429 15.5942 9.94353 15.6795 9.873L18.8873 7.125C18.9488 7.07491 19.0247 7.04566 19.104 7.04145C19.1832 7.03724 19.2618 7.05828 19.3283 7.10156C19.3949 7.14483 19.446 7.2081 19.4743 7.28226C19.5025 7.35643 19.5066 7.43765 19.4858 7.51425L17.3603 15.1987C17.3169 15.356 17.2234 15.4948 17.094 15.5942C16.9646 15.6935 16.8064 15.7479 16.6433 15.7492H7.35751C7.19426 15.7481 7.03585 15.6937 6.90631 15.5944C6.77677 15.495 6.68318 15.3561 6.63976 15.1987L4.51501 7.515C4.4942 7.4384 4.49823 7.35718 4.52652 7.28301C4.55481 7.20885 4.6059 7.14558 4.67244 7.10231C4.73898 7.05903 4.81753 7.03799 4.89679 7.0422C4.97605 7.04641 5.05194 7.07566 5.11351 7.12575L8.32051 9.87375C8.40586 9.94428 8.50577 9.99504 8.61306 10.0224C8.72035 10.0497 8.83237 10.053 8.94106 10.0319C9.04976 10.0108 9.15244 9.96593 9.24173 9.90046C9.33102 9.83499 9.40472 9.75057 9.45751 9.65325L11.6715 5.4495Z" stroke="#FDC500" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                    <clipPath id="clip0_105_18">
                    <rect width="24" height="24" fill="white"/>
                    </clipPath>
                    </defs>
                  </svg>
                </SoftBox>
            )}
          </SoftBox>
          <SoftTypography
            sx={{
              // Tile primary value — unified big-number token: 20/600 tabular
              // (aligned with King of the Mountain's point numbers).
              fontFamily: "DM Sans",
              color: darkMode ? "#FFFFFF" : "#0F172A",
              fontSize: { mini: "20px", wide: "3.6vh" },
              fontWeight: 600,
              fontFeatureSettings: '"tnum" 1',
              letterSpacing: "-0.01em",
              lineHeight: 1.15,
            }}
          >
            {fmt(amount)}&nbsp;
          </SoftTypography>
          <SoftTypography
            sx={{
              // Trend value — was 14/400-500. Now 12/500, still color-coded
              // green/red, but visibly smaller than the main value.
              fontFamily: "DM Sans",
              color: kpi.gap
                ? darkMode
                  ? "#FFFFFF"
                  : "#000000"
                : isLeading
                ? "positive.main"
                : "negative.main",
              fontSize: { mini: "12px", wide: "1.5vh" },
              fontWeight: 500,
              fontFeatureSettings: '"tnum" 1',
            }}
          >
            {!kpi.gap
              ? `${yetiDiff > 0 ? plus : ""}${fmt(yetiDiff)}`
              : fmt(kpi.target)}
          </SoftTypography>
        </SoftBox>
        {!isSmallScreen && (
          <MiniChart kpi={kpi} playerAmount={playerAmt} yetiStats={yeti} />
        )}
      </SoftBox>
    );
  },
);

export default ItemBox;
